import { listDocName, type ListItem } from '@awwyeah/shared';
import { ChevronLeft, Mail, MoreVertical, ShoppingCart } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useYArray, useYDoc, useYMap } from '../../sync';
import { useUser } from '../auth';
import { Fab } from '../../components/Fab';
import { ListItemRow } from './components/ListItemRow';
import { Sheet } from './components/Sheet';
import { MemberAvatar } from './components/MemberAvatar';
import { buildListMailto } from './mailto';
import type { RosterMember } from './useHouseholdRoster';
import {
  addItem,
  buildHeaderSegments,
  buildSectionGroups,
  deleteItem,
  reorderItem,
  splitCheckedUnchecked,
  splitListInput,
  toggleItem,
  updateItem,
} from './listOps';
import { useHouseholdRoster } from './useHouseholdRoster';
import { useListMeta } from './useListRegistries';
import { useRemoteChangeFlash } from './useRemoteChangeFlash';

/**
 * Inline "add" row. `focusToken` is a counter the page bumps when the floating add button
 * is pressed: any change opens this row and focuses its input, which is how the loud
 * accent button and the quiet inline rows stay one feature rather than two.
 */
function AddRow({
  placeholder,
  onAdd,
  focusToken,
}: {
  placeholder: string;
  onAdd: (text: string) => void;
  focusToken?: number;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const rowRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (focusToken === undefined || focusToken === 0) return;
    setOpen(true);
    // Next frame: the input only exists once the open state has painted.
    requestAnimationFrame(() => rowRef.current?.scrollIntoView({ block: 'center' }));
  }, [focusToken]);

  if (!open) {
    return (
      <button
        type="button"
        data-testid="add-row-trigger"
        ref={(element) => {
          rowRef.current = element;
        }}
        className="flex min-h-[48px] items-center gap-3 border-b border-dashed border-line px-4 py-2 text-left last:border-b-0"
        onClick={() => setOpen(true)}
      >
        <span className="h-[26px] w-[26px] flex-shrink-0 rounded border-2 border-dashed border-line" />
        <span className="text-base text-fg-3">{placeholder}</span>
      </button>
    );
  }
  const submit = (): void => {
    const trimmed = text.trim();
    if (trimmed) onAdd(trimmed);
    setText('');
  };
  return (
    <div
      className="flex min-h-[48px] items-center gap-3 border-b border-line px-4 py-2 last:border-b-0"
      ref={(element) => {
        rowRef.current = element;
      }}
    >
      <span className="h-[26px] w-[26px] flex-shrink-0 rounded border-2 border-dashed border-line" />
      <input
        data-testid="add-item-input"
        autoFocus
        className="min-w-0 flex-1 bg-transparent text-base text-fg outline-none"
        placeholder="Add an item…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            // Keep the input open and focused so a keyboard user can chain items.
            e.preventDefault();
            submit();
          }
          if (e.key === 'Escape') setOpen(false);
        }}
        onPaste={(e) => {
          const parts = splitListInput(e.clipboardData.getData('text'));
          if (parts.length > 1) {
            e.preventDefault();
            for (const part of parts) onAdd(part);
            setText('');
          }
        }}
        onBlur={() => {
          submit();
          setOpen(false);
        }}
      />
    </div>
  );
}

function AssigneeSheet({
  item,
  members,
  onClose,
  onToggle,
}: {
  item: ListItem | null;
  members: RosterMember[];
  onClose: () => void;
  onToggle: (memberId: string) => void;
}): JSX.Element {
  if (!item) return <></>;
  return (
    <Sheet open onClose={onClose} title={`Assign "${item.text}"`} testId="assign-sheet">
      <div className="flex flex-col gap-2 pb-2">
        {members.map((member) => {
          const active = item.assigneeIds.includes(member.id);
          return (
            <button
              key={member.id}
              type="button"
              data-testid="assign-member-option"
              data-active={active}
              onClick={() => onToggle(member.id)}
              className={`flex h-14 items-center gap-3 rounded-md px-2 text-left ${active ? 'bg-raised' : ''}`}
            >
              <MemberAvatar member={member} />
              <span className="flex-1 text-base font-medium text-fg">{member.name}</span>
              {active && <span className="text-sm font-semibold text-member-faye">✓</span>}
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}

export function ListDetailPage(): JSX.Element {
  const { listId } = useParams<{ listId: string }>();
  const { user } = useUser();
  const navigate = useNavigate();
  const { getMember, meId, members } = useHouseholdRoster();
  const [assignItemId, setAssignItemId] = useState<string | null>(null);

  // WS-15: a list's meta lives in the shared registry *or* the owner's personal one, so
  // this reads through the merge rather than the `lists` doc directly.
  const { meta: list, map: listsMap } = useListMeta(listId);

  const doc = useYDoc(listId ? listDocName(listId) : listDocName('missing'));
  const { array: order } = useYArray<string>(doc, 'order');
  const { map, entries } = useYMap<ListItem>(doc, 'items');
  const items = entries.map(([, item]) => item);

  const [menuOpen, setMenuOpen] = useState(false);
  const [showDone, setShowDone] = useState(false);
  // Bumped by the floating add button; the first AddRow on the page watches it.
  const [addFocusToken, setAddFocusToken] = useState(0);
  const dragRef = useRef<{
    id: string;
    overId: string | null;
    position: 'before' | 'after';
  } | null>(null);
  const [, forceRender] = useState(0);

  const flashing = useRemoteChangeFlash(items, meId);

  if (!user || !listId) return <></>;

  if (!list) {
    return (
      <div
        className="flex flex-col items-center gap-3 py-16 text-center"
        data-testid="list-not-found"
      >
        <p className="text-sm text-fg-3">This list isn't here anymore.</p>
        <button
          type="button"
          className="text-sm font-semibold text-member-faye"
          onClick={() => navigate('/lists')}
        >
          Back to lists
        </button>
      </div>
    );
  }

  const { checked } = splitCheckedUnchecked(items);
  const uncheckedTotal = items.filter((i) => !i.header && !i.checked).length;

  // Flat, order-sorted view of the *unchecked* rows only — what a drag actually reorders
  // against, regardless of which visual group (header segment / store section) it's in.
  const flatUnchecked = [...items].filter((i) => !i.checked).sort((a, b) => a.order - b.order);

  const onAdd = (text: string, section?: string): void => {
    addItem(doc, order, map, { text, userId: user.id, section });
  };

  const addHeader = (): void => {
    addItem(doc, order, map, { text: 'New group', userId: user.id, header: true });
  };

  const onDrop = (): void => {
    const drag = dragRef.current;
    dragRef.current = null;
    forceRender((n) => n + 1);
    if (!drag || !drag.overId || drag.overId === drag.id) return;
    const idx = flatUnchecked.findIndex((i) => i.id === drag.overId);
    if (idx === -1) return;
    const beforeId =
      drag.position === 'before' ? flatUnchecked[idx - 1]?.id : flatUnchecked[idx]?.id;
    const afterId =
      drag.position === 'before' ? flatUnchecked[idx]?.id : flatUnchecked[idx + 1]?.id;
    reorderItem(doc, map, drag.id, beforeId, afterId, user.id);
  };

  const onHandlePointerDown =
    (itemId: string) =>
    (event: React.PointerEvent): void => {
      event.preventDefault();
      dragRef.current = { id: itemId, overId: null, position: 'after' };
      const move = (e: PointerEvent): void => {
        const el = document
          .elementFromPoint(e.clientX, e.clientY)
          ?.closest<HTMLElement>('[data-item-id]');
        if (!el || !dragRef.current) return;
        const rect = el.getBoundingClientRect();
        dragRef.current.overId = el.dataset['itemId'] ?? null;
        dragRef.current.position = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
        forceRender((n) => n + 1);
      };
      const up = (): void => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        onDrop();
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    };

  const emailHref = buildListMailto(list, items, (id) => getMember(id).name);
  const dragOverId = dragRef.current?.overId;

  const renderItem = (item: ListItem, reorderable: boolean): JSX.Element => (
    <ListItemRow
      key={item.id}
      item={item}
      variant="todo"
      getMember={getMember}
      meId={meId}
      flashing={flashing.has(item.id)}
      reorderable={reorderable}
      onToggle={() => toggleItem(doc, map, item.id, user.id)}
      onDelete={() => deleteItem(doc, order, map, item.id)}
      onAssign={() => setAssignItemId(item.id)}
      onTextChange={(text) => updateItem(doc, map, item.id, user.id, { text })}
      onDragHandlePointerDown={onHandlePointerDown(item.id)}
    />
  );
  const assignItem = items.find((i) => i.id === assignItemId) ?? null;
  const toggleAssignee = (memberId: string): void => {
    if (!assignItem) return;
    const has = assignItem.assigneeIds.includes(memberId);
    updateItem(doc, map, assignItem.id, user.id, {
      assigneeIds: has
        ? assignItem.assigneeIds.filter((id) => id !== memberId)
        : [...assignItem.assigneeIds, memberId],
    });
  };

  return (
    <div className="flex flex-col gap-0 py-2" data-testid="list-detail-page">
      <header className="-mx-4 flex items-center gap-2 border-b border-line bg-surface px-2 py-2 sm:mx-0 sm:rounded-t-md">
        <button
          type="button"
          aria-label="Back to lists"
          data-testid="back-button"
          onClick={() => navigate('/lists')}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-lg tracking-tight text-fg">
            {list.emoji ? `${list.emoji} ` : ''}
            {list.name}
          </div>
          <div className="text-xs text-fg-3">
            {uncheckedTotal} to go · {checked.length} done
          </div>
        </div>
        <a
          href={emailHref}
          data-testid="email-list-link"
          aria-label="Email this list"
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center text-fg-2"
        >
          <Mail size={18} />
        </a>
        <button
          type="button"
          aria-label="List options"
          data-testid="list-options-button"
          onClick={() => setMenuOpen(true)}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center text-fg-2"
        >
          <MoreVertical size={18} />
        </button>
      </header>

      {/* The only "Start shopping" in the app now (usability testing 2026-08-01 removed the
          duplicate on the index card): full width, 56px tall, top of the list. */}
      {list.type === 'shopping' && (
        <button
          type="button"
          data-testid="shopping-mode-toggle"
          className="my-3 flex h-14 w-full items-center justify-center gap-2 rounded-md bg-inverse text-base font-semibold text-inverse-fg"
          onClick={() => navigate(`/lists/${listId}/shop`)}
        >
          <ShoppingCart size={18} /> Start shopping
        </button>
      )}

      <div
        className="flex flex-col overflow-hidden rounded-md border border-line bg-surface"
        data-testid="list-groups"
      >
        {items.length === 0 && (
          <>
            <p className="px-4 py-6 text-sm text-fg-3" data-testid="list-groups-empty">
              {list.type === 'shopping'
                ? "An empty list is a beautiful thing. Type the first item and it'll show up on everyone's phone."
                : 'Nothing here yet — add a group or an item below.'}
            </p>
            <AddRow placeholder="Add something…" onAdd={onAdd} focusToken={addFocusToken} />
          </>
        )}
        {list.type === 'shopping'
          ? buildSectionGroups(items).map((group, groupIndex) => (
              <div key={group.name}>
                <div className="flex h-9 items-center justify-between border-b border-line bg-raised px-4">
                  <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">
                    {group.name}
                  </span>
                  <span className="text-xs text-fg-3">
                    {group.items.filter((i) => !i.checked).length}
                  </span>
                </div>
                {group.items
                  .filter((i) => !i.checked)
                  .map((item) => (
                    <div
                      key={item.id}
                      className={dragOverId === item.id ? 'border-t-2 border-accent' : ''}
                    >
                      {renderItem(item, true)}
                    </div>
                  ))}
                <AddRow
                  placeholder={
                    group.name === 'Everything else' ? 'Add something…' : `Add to ${group.name}…`
                  }
                  {...(groupIndex === 0 ? { focusToken: addFocusToken } : {})}
                  onAdd={(text) =>
                    onAdd(text, group.name === 'Everything else' ? undefined : group.name)
                  }
                />
              </div>
            ))
          : buildHeaderSegments(items).map((segment, i) => (
              <div key={segment.header?.id ?? `leading-${i}`}>
                {segment.header && (
                  <div className="flex h-9 items-center justify-between border-b border-line bg-raised px-4">
                    <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">
                      {segment.header.text}
                    </span>
                    <span className="text-xs text-fg-3">{segment.items.length} left</span>
                  </div>
                )}
                {segment.items.map((item) => (
                  <div
                    key={item.id}
                    className={dragOverId === item.id ? 'border-t-2 border-accent' : ''}
                  >
                    {renderItem(item, true)}
                  </div>
                ))}
                <AddRow
                  placeholder={
                    segment.header ? `Add to "${segment.header.text}"…` : 'Add something…'
                  }
                  {...(i === 0 ? { focusToken: addFocusToken } : {})}
                  onAdd={onAdd}
                />
              </div>
            ))}
      </div>

      {list.type !== 'shopping' && (
        <button
          type="button"
          data-testid="add-group-button"
          className="mt-2 self-start text-sm font-semibold text-member-faye"
          onClick={addHeader}
        >
          + Add group
        </button>
      )}

      {checked.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            data-testid="done-section-toggle"
            className="flex h-9 w-full items-center justify-between border-b border-line bg-bg px-4"
            onClick={() => setShowDone((v) => !v)}
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-fg-3">
              Done · {checked.length}
            </span>
            <span className="text-xs text-fg-3">{showDone ? 'Hide' : 'Show'}</span>
          </button>
          {showDone && (
            <div className="bg-bg" data-testid="done-section">
              {checked.map((item) => (
                <ListItemRow
                  key={item.id}
                  item={item}
                  variant="todo"
                  getMember={getMember}
                  meId={meId}
                  flashing={flashing.has(item.id)}
                  onToggle={() => toggleItem(doc, map, item.id, user.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <Sheet
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        title={list.name}
        testId="list-detail-menu"
      >
        <div className="flex flex-col">
          <a
            href={emailHref}
            data-testid="email-list-action"
            className="flex h-14 items-center gap-2 border-b border-line text-left text-base font-medium text-fg"
          >
            <Mail size={16} /> Email this list
          </a>
          <button
            type="button"
            data-testid="toggle-archive-action"
            className="flex h-14 items-center text-left text-base font-medium text-fg"
            onClick={() => {
              listsMap?.set(list.id, {
                ...list,
                archived: !list.archived,
                updatedBy: user.id,
                updatedAt: new Date().toISOString(),
              });
              setMenuOpen(false);
              navigate('/lists');
            }}
          >
            {list.archived ? 'Unarchive' : 'Archive'}
          </button>
        </div>
      </Sheet>

      <Fab
        label="Add something to this list"
        testId="list-add-fab"
        onClick={() => setAddFocusToken((token) => token + 1)}
      />

      <AssigneeSheet
        item={assignItem}
        members={[...members.values()]}
        onClose={() => setAssignItemId(null)}
        onToggle={toggleAssignee}
      />
    </div>
  );
}
